// src/pages/CoworkPage.js
//
// "Cowork" — a chat surface backed by the Hermes agent.
// Mirrors the AdminChatPage data-flow (POST /cowork/ask -> jobId,
// then EventSource on the shared /api/chat/jobs/<jobId>/stream),
// but kept intentionally lean: a single ongoing conversation whose
// sessionId is persisted in localStorage.

import React, { useEffect, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

import { askCowork, loadCoworkSession, getStreamToken } from "../api/coworkApi";

const SESSION_KEY = "cowork_session_id";

export default function CoworkPage() {
  const [messages, setMessages] = useState([]);
  const [composer, setComposer] = useState("");
  const [sending, setSending] = useState(false);
  const [sessionId, setSessionId] = useState(
    () => localStorage.getItem(SESSION_KEY) || ""
  );

  const sseRef = useRef(null);
  const scrollRef = useRef(null);

  // Load prior history for the persisted session, if any.
  useEffect(() => {
    if (!sessionId) return;
    const ctrl = new AbortController();
    (async () => {
      try {
        const data = await loadCoworkSession(sessionId, { signal: ctrl.signal });
        if (data?.messages) setMessages(data.messages);
      } catch {
        // Stale/unknown session — start fresh.
        localStorage.removeItem(SESSION_KEY);
        setSessionId("");
      }
    })();
    return () => ctrl.abort();
  }, [sessionId]);

  // Auto-scroll to the latest message.
  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages, sending]);

  // Tidy up the SSE connection on unmount.
  useEffect(() => {
    return () => {
      try {
        sseRef.current?.close();
      } catch {
        // ignore
      }
    };
  }, []);

  function appendMessage(msg) {
    setMessages((prev) => [...prev, msg]);
  }

  async function send() {
    const text = composer.trim();
    if (!text || sending) return;

    setSending(true);
    setComposer("");

    appendMessage({
      id: `tmp-user-${Date.now()}`,
      role: "user",
      text,
      ts: new Date().toISOString(),
    });

    let finished = false;

    try {
      const data = await askCowork(sessionId, text);
      const jobId = data?.jobId;
      const newSessionId = data?.sessionId;

      if (newSessionId && newSessionId !== sessionId) {
        setSessionId(newSessionId);
        localStorage.setItem(SESSION_KEY, newSessionId);
      }

      if (!jobId) throw new Error("Missing jobId from server.");

      const token = getStreamToken();
      const es = new EventSource(
        `/api/chat/jobs/${jobId}/stream?token=${encodeURIComponent(token)}`
      );
      sseRef.current = es;

      const finalize = () => {
        if (finished) return;
        finished = true;
        try {
          es.close();
        } catch {
          // ignore
        }
        if (sseRef.current === es) sseRef.current = null;
        setSending(false);
      };

      es.addEventListener("done", (ev) => {
        try {
          const payload = JSON.parse(ev.data || "{}");
          appendMessage({
            id: `tmp-asst-${Date.now()}`,
            role: "assistant",
            text: payload?.assistantMessage ?? "",
            metadata: { timingMs: payload?.timingMs },
            ts: new Date().toISOString(),
          });
        } catch {
          appendMessage({
            id: `tmp-sys-${Date.now()}`,
            role: "system",
            text: "Error: failed to parse stream result.",
            ts: new Date().toISOString(),
          });
        } finally {
          finalize();
        }
      });

      es.addEventListener("error", () => {
        appendMessage({
          id: `tmp-sys-${Date.now()}`,
          role: "system",
          text: "Error: stream disconnected or the agent failed.",
          ts: new Date().toISOString(),
        });
        finalize();
      });
    } catch (e) {
      appendMessage({
        id: `tmp-sys-${Date.now()}`,
        role: "system",
        text: `Error: ${e?.message || "request failed."}`,
        ts: new Date().toISOString(),
      });
      setSending(false);
    }
  }

  function newConversation() {
    try {
      sseRef.current?.close();
    } catch {
      // ignore
    }
    sseRef.current = null;
    localStorage.removeItem(SESSION_KEY);
    setSessionId("");
    setMessages([]);
    setComposer("");
    setSending(false);
  }

  function onKeyDown(e) {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  }

  return (
    <div style={styles.wrap}>
      <div style={styles.header}>
        <div>
          <div style={styles.title}>Cowork</div>
          <div style={styles.subtitle}>Work alongside the Aegis agent</div>
        </div>
        <button style={styles.newBtn} onClick={newConversation} type="button">
          New conversation
        </button>
      </div>

      <div ref={scrollRef} style={styles.messages}>
        {messages.length === 0 && !sending && (
          <div style={styles.empty}>
            Ask the agent to help with a task to get started.
          </div>
        )}

        {messages.map((m) => (
          <div
            key={m.id}
            style={{
              ...styles.row,
              justifyContent: m.role === "user" ? "flex-end" : "flex-start",
            }}
          >
            <div
              style={{
                ...styles.bubble,
                ...(m.role === "user"
                  ? styles.userBubble
                  : m.role === "system"
                  ? styles.systemBubble
                  : styles.assistantBubble),
              }}
            >
              {m.role === "assistant" ? (
                <ReactMarkdown remarkPlugins={[remarkGfm]}>
                  {m.text || ""}
                </ReactMarkdown>
              ) : (
                <span style={{ whiteSpace: "pre-wrap" }}>{m.text}</span>
              )}
            </div>
          </div>
        ))}

        {sending && (
          <div style={{ ...styles.row, justifyContent: "flex-start" }}>
            <div style={{ ...styles.bubble, ...styles.assistantBubble }}>
              <span style={styles.thinking}>Thinking…</span>
            </div>
          </div>
        )}
      </div>

      <div style={styles.composerRow}>
        <textarea
          style={styles.textarea}
          value={composer}
          onChange={(e) => setComposer(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder="Message the agent…  (Enter to send, Shift+Enter for newline)"
          rows={2}
          disabled={sending}
        />
        <button
          style={{
            ...styles.sendBtn,
            ...(sending || !composer.trim() ? styles.sendBtnDisabled : {}),
          }}
          onClick={send}
          disabled={sending || !composer.trim()}
          type="button"
        >
          Send
        </button>
      </div>
    </div>
  );
}

const styles = {
  wrap: {
    display: "flex",
    flexDirection: "column",
    height: "calc(100vh - 64px)",
    maxWidth: 900,
    margin: "0 auto",
    padding: "12px 16px",
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    paddingBottom: 10,
    borderBottom: "1px solid #e5e7eb",
  },
  title: { fontSize: 20, fontWeight: 600 },
  subtitle: { fontSize: 13, color: "#6b7280" },
  newBtn: {
    border: "1px solid #d1d5db",
    background: "#fff",
    borderRadius: 8,
    padding: "6px 12px",
    fontSize: 13,
    cursor: "pointer",
  },
  messages: {
    flex: 1,
    overflowY: "auto",
    padding: "16px 4px",
    display: "flex",
    flexDirection: "column",
    gap: 10,
  },
  empty: { color: "#9ca3af", textAlign: "center", marginTop: 40 },
  row: { display: "flex", width: "100%" },
  bubble: {
    maxWidth: "78%",
    padding: "10px 14px",
    borderRadius: 14,
    fontSize: 15,
    lineHeight: 1.5,
    wordBreak: "break-word",
  },
  userBubble: { background: "#2563eb", color: "#fff", borderBottomRightRadius: 4 },
  assistantBubble: {
    background: "#f3f4f6",
    color: "#111827",
    borderBottomLeftRadius: 4,
  },
  systemBubble: { background: "#fef2f2", color: "#b91c1c", fontSize: 13 },
  thinking: { color: "#6b7280", fontStyle: "italic" },
  composerRow: {
    display: "flex",
    gap: 8,
    paddingTop: 10,
    borderTop: "1px solid #e5e7eb",
  },
  textarea: {
    flex: 1,
    resize: "none",
    borderRadius: 10,
    border: "1px solid #d1d5db",
    padding: "10px 12px",
    fontSize: 15,
    fontFamily: "inherit",
  },
  sendBtn: {
    border: "none",
    background: "#2563eb",
    color: "#fff",
    borderRadius: 10,
    padding: "0 20px",
    fontSize: 15,
    fontWeight: 600,
    cursor: "pointer",
  },
  sendBtnDisabled: { background: "#9ca3af", cursor: "not-allowed" },
};
